import { useMemo } from "react";
import { object, func, bool, number } from "prop-types";
import { observer}  from "mobx-react";
import { useTranslation } from "react-i18next";
import { useEstimateEditorStore } from "@tools";
import { ClearInput, Checkbox } from "@components";
import { LabelColorPicker } from "@components/LabelEditor/components/LabelColorPicker";
import { Grid, IconButton } from "@material-ui/core";
import { ModuleArrow } from "@assets";
import { ExpandLess, ExpandMore } from "@material-ui/icons";
import useStyle from "./TimelineSettings.style";
import classnames from "classnames";

export const TimelineSettingsRow = observer(({
  item,
  depth,
  onColor,
  onFontColor,
  onAllocation,
  showVisibility,
  showAllocation = true,
  showExpandButton,
  showArrow,
  showHidden,
  onVisibilityChange,
  expanded,
  onExpand,
}) => {
  const { t } = useTranslation();
  const editorStore = useEstimateEditorStore();
  const classes = useStyle();

  const pickerSize = useMemo(() => {
    if(depth === 0)
      return "large";
    if(depth === 1)
      return "medium";
    return "small";
  }, [ depth ]);

  return (
    <Grid
      item
      container
      wrap="nowrap"
      alignItems="center"
      justifyContent="space-between"
    >
      <Grid item container className={classes.titleCell}>
        {
          showExpandButton
          ? <IconButton
              size="small"
              onClick={onExpand}
              aria-label={expanded ? "Collapse" : "Expand"}
              className="mr-1"
            >
              {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </IconButton>
            : showArrow &&
              <Grid
                item
                container
                justifyContent="center"
                alignItems="center"
                className="w-24 h-24"
              >
                <ModuleArrow />
              </Grid>
        }
        <span
          className={classnames(
            depth === 0 && "font-bolder",
            depth < 2 && "text-md",
            depth === 2 && "text-sm",
            !item.name?.length ? "semi-transparent" : ( depth === 2 && "transparent-3"),
          )}
        >
          { item.name ||  t("common.unnamed") }
        </span>
      </Grid>
      <Grid item container className={classnames(classes.cell, classes.valueCell)}>
        <span className="text-secondary">
          { `${item.parsedWorkload || 0} h` }
        </span>
      </Grid>
      <Grid item container className={classnames(classes.cell, classes.valueCell)}>
        {showAllocation && <ClearInput
          value={item.hourlyTimeAllocation}
          min={0}
          max={24}
          showDecimals
          onChange={(value) => onAllocation(Number(value))}
          suffix="h / day"
          fullWidth
        />}
      </Grid>
      <Grid item container className={classnames(classes.cell, classes.colorCell)}>
        <LabelColorPicker
          size={pickerSize}
          backgroundColor={item.color}
          fontColor={item.fontColor}
          onColorChange={onColor}
          onFontColorChange={onFontColor}
          additionalColors={editorStore.additionalColors}
          additionalFontColors={editorStore.additionalFontColors}
          onAdditionalColorChange={onColor}
          onAdditionalColorConfirm={editorStore.addAdditionalColor}
          onAdditionalFontColorChange={onFontColor}
          onAdditionalFontColorConfirm={editorStore.addAdditionalFontColor}
        />
      </Grid>
      {
        showVisibility &&
        <Grid item container className={classnames(classes.cell, classes.valueCell)}>
          {
            showHidden &&
            <Checkbox
              name="hide section"
              checked={!item.hidden}
              onChange={onVisibilityChange}
            />
          }
        </Grid>
      }
    </Grid>
  );
});

TimelineSettingsRow.propTypes = {
  item: object.isRequired,
  depth: number.isRequired,
  onColor: func.isRequired,
  onFontColor: func.isRequired,
  onAllocation: func.isRequired,
  showVisibility: bool,
  showAllocation: bool,
  showExpandButton: bool,
  showArrow: bool,
  showHidden: bool,
  onVisibilityChange: func,
  expanded: bool,
  onExpand: func,
};
